/**
 * This controls all the side menu logic, rendering the children in the middle with the side menu, if visible.
 */

import useIsUserSideMenuRendered from '@apps/www/src/www/hooks/useIsUserSideMenuRendered';
import usePersistentSetting, {
	PersistentSettingKeys,
} from '@apps/www/src/www/hooks/usePersistentSetting';
import useUserAndBoardPageQuery from '@apps/www/src/www/hooks/useUserAndBoardPageQuery';
import SVNewBoardModal from '@apps/www/src/www/modals/SVNewBoardModal';
import SVModal from '@pkgs/shared-client/components/SVModal';
import SVSidebarContent, {
	SVSidebarContentTYPES,
} from '@pkgs/shared-client/components/SVSidebarContent';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import BoardOwnershipType from '@pkgs/shared/enums/BoardOwnershipType';
import SidebarOpenState from '@pkgs/shared/enums/SidebarOpenState';
import React from 'react';

const SVUserSideMenuContainer = ({ children }: { children: React.ReactNode }) => {
	const [sidebarOpenState, setSidebarOpenState] = usePersistentSetting(
		PersistentSettingKeys.SIDEBAR_OPEN_STATE,
	);
	const isSideMenuRendered = useIsUserSideMenuRendered();
	const { isOwner, user } = useUserAndBoardPageQuery();

	const handleToggleClick = useEventCallback(() => {
		const stateValues = Object.values(SidebarOpenState);

		// cycle through the states
		const newState =
			stateValues[(stateValues.indexOf(sidebarOpenState) + 1) % stateValues.length];

		setSidebarOpenState(newState);
	});

	const handleNewBoardClick = useEventCallback(() => {
		SVModal.open(SVNewBoardModal, {
			ownershipType: BoardOwnershipType.USER,
		});
	});

	// If user doesn't have boards we don't want to render the side menu at all
	if (!user || !isSideMenuRendered) {
		return <>{children}</>;
	}

	return (
		<SVSidebarContent
			type={SVSidebarContentTYPES.USER}
			openState={sidebarOpenState}
			boards={user.boards.filter((board) => board.ownershipType === BoardOwnershipType.USER)}
			isOwner={isOwner}
			onNewBoardClick={handleNewBoardClick}
			onToggleSidebar={handleToggleClick}
		>
			{children}
		</SVSidebarContent>
	);
};

export default SVUserSideMenuContainer;
